import ListLayout from 'app/components/Layout/ListLayout';
import { getCurrentNav } from 'app/config/routes';
import React, { useEffect } from 'react';
import classes from './AccountManagement.module.scss';
import { notification } from 'antd';
import { RocketOutlined } from '@ant-design/icons';
import Table from 'app/components/Table';
import { useNavigate } from 'react-router';
import {
  useLazyGetInspectorsQuery,
  useDisableInspectorMutation,
  useActiveInspectorMutation,
  useInactiveInspectorMutation
} from 'app/features/account-management/api';
import { getColumnDefs } from './constants';
import { get, pick } from 'lodash';
import styled from '@emotion/styled';
import FilterBar, { EFilterItemType } from 'app/components/FilterBar';
import { useCustomFilter } from 'app/hooks/useCustomFilter';

const StyledTotalLabel = styled.p`
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.005em;
  text-align: left;
  color: #8991a0;
`;

export default function AccountList() {
  const nav = getCurrentNav();
  const navigate = useNavigate();
  const {
    pagination,
    setPagination,
    filters,
    setFilters,
    searchKeyword,
    setSearchKeyword
  } = useCustomFilter({
    resource: 'account-management',
    syncWithStore: true,
    pagination: {
      current: 1,
      pageSize: 10
    },
    filters: {
      initial: [
        {
          field: 'status',
          operator: 'eq',
          value: []
        }
      ]
    }
  });
  const [fetch, result] = useLazyGetInspectorsQuery();
  const [disable] = useDisableInspectorMutation();
  const [active] = useActiveInspectorMutation();
  const [inactive] = useInactiveInspectorMutation();

  const handleAddNewUser = () => {
    navigate('creating-inspector');
  };

  const onNavigateToInspector = (inspectorId: string) => {
    navigate(`inspector/${inspectorId}`);
  };

  const disableInspector = async (inspectorId: string) => {
    try {
      const response: any = await disable(inspectorId);
      if (response.error) {
        const message = get(response.error.data, 'msg.message');
        notification.error({ message, placement: 'bottomLeft' });
        return;
      }
      notification.success({
        message: 'User disabled!',
        placement: 'bottomLeft'
      });
      fetch({
        ...(result.originalArgs as any)
      });
    } catch (error) {
      console.log('Disable-Inspector-Error', error);
    }
  };
  const activeInspector = async (inspectorId: string) => {
    try {
      const response: any = await active(inspectorId);
      if (response.error) {
        const message = get(response.error.data, 'msg.message');
        notification.error({ message, placement: 'bottomLeft' });
        return;
      }
      notification.success({
        message: 'User active!',
        placement: 'bottomLeft'
      });
      fetch({
        ...(result.originalArgs as any)
      });
    } catch (error) {
      console.log('Disable-Inspector-Error', error);
    }
  };
  const inactiveInspector = async (inspectorId: string) => {
    try {
      const response: any = await inactive(inspectorId);
      if (response.error) {
        const message = get(response.error.data, 'msg.message');
        notification.error({ message, placement: 'bottomLeft' });
        return;
      }
      notification.success({
        message: 'User inactive!',
        placement: 'bottomLeft'
      });
      fetch({
        ...(result.originalArgs as any)
      });
    } catch (error) {
      console.log('Disable-Inspector-Error', error);
    }
  };

  const handleSearch = (keyword) => {
    setSearchKeyword(keyword, {
      current: 1
    });
  };

  const handleChangeFilter = (filter) => {
    const nextFilters = Object.keys(filter || {}).reduce((acc: any, key) => {
      if (filter[key]) {
        acc.push({
          field: key,
          value: filter[key]?.filter((item) => item !== 'all')
        });
      }
      return acc;
    }, []);

    setFilters(nextFilters, {
      ...pagination,
      current: 1
    });
  };

  const handleChangePagination = (nextPagination) => {
    setPagination({
      current: nextPagination.current,
      pageSize: nextPagination.pageSize
    });
  };

  const userColumns = getColumnDefs(
    onNavigateToInspector,
    disableInspector,
    activeInspector,
    inactiveInspector
  );

  useEffect(() => {
    const params = {
      keywords: searchKeyword,
      ...pagination,
      ...filters?.reduce((acc, filter) => {
        if ('field' in filter && filter.value) {
          return {
            ...acc,
            [filter.field]: filter.value
          };
        }
        return acc;
      }, {})
    };
    if (!searchKeyword) {
      delete params['keywords'];
    }
    fetch(params as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, filters, searchKeyword]);

  return (
    <ListLayout
      createButtonText="Add New User"
      onCreate={handleAddNewUser}
      onSearch={handleSearch}
      searchInputProps={{
        placeholder: 'Search User Name , email, contact no.',
        defaultValue: searchKeyword
      }}
      icon={
        nav.icon
          ? React.cloneElement(nav.icon as any, { className: classes.icon })
          : null
      }
      title={
        <span className={classes.title}>
          Inspector Management
          {/* <Tag color="blue">
            <Icon component={UserIcon}></Icon>
            No. of Users : 3/8
          </Tag> */}
        </span>
      }
    >
      <FilterBar
        value={{
          //TODO: Get From the filters and sorters.
          ...pick(result.originalArgs, ['status'])
        }}
        defaultValue={{
          status: []
        }}
        onChange={handleChangeFilter}
        filterItems={[
          {
            key: 'status',
            type: EFilterItemType.DROPDOWN,
            haveOptionAll: true,
            label: (selected) => {
              if (selected.includes('all')) {
                return 'All Status';
              }
              return `Status (${selected.length})`;
            },
            icon: <RocketOutlined />,
            options: [
              {
                value: 'active',
                label: 'Active'
              },
              {
                value: 'inactive',
                label: 'Inactive'
              },
              {
                value: 'disabled',
                label: 'Disabled'
              }
            ]
          }
        ]}
      ></FilterBar>
      <div className={classes.content}>
        <Table
          loading={result.isLoading || result.isFetching}
          columns={userColumns}
          dataSource={result.data?.data}
          onChange={(pagination) => {
            handleChangePagination(pagination);
          }}
          pagination={{
            ...(result.data as any)?.pagination,
            ...pagination,
            showSizeChanger: true,
            showTotal: (total, range) => {
              return (
                <StyledTotalLabel>
                  {range[0]}-{range[1]} of {total}
                </StyledTotalLabel>
              );
            }
          }}
        ></Table>
      </div>
    </ListLayout>
  );
}
